import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Alert,
	AlertIcon,
	Box,
	Button,
	Flex,
	Grid,
	HStack,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Select,
	Spinner,
	Tab,
	Table,
	TableContainer,
	TabList,
	TabPanel,
	TabPanels,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tabs,
	Tr,
	useDisclosure,
	useToast,
	VStack
} from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { ChangeEvent, FunctionComponent, ReactElement, useRef, useState } from "react";

// Components
import LayoutWrapper from "../../shared/layout";
import { TabPage } from "../../shared/layout/index.styles";
import ImportReportsModal from "./ImportReportsModal";
import { importClaimReports } from "../../utils/api/reports";
import { ClaimReportImportError } from "../../utils/types";

interface ReportsPageProps {}

enum ReportsTabs {
	Import,
	Export
}

interface ImportSummary {
	totalRows: number;
	successfulRows: number;
	failedRows: number;
}

const getImportErrorMessage = (err: unknown): string => {
	const rpcError = err as RpcError;
	if (typeof rpcError?.message === "string" && rpcError.message.trim().length > 0) {
		return rpcError.message;
	}

	if (err instanceof Error && err.message.trim().length > 0) {
		return err.message;
	}

	return "There was a problem importing reports.";
};

/**
 * Handles the Reports page for generating and importing claim reports
 * @param props typeof: ReportsPageProps
 * @returns ReactElement
 */
const ReportsPage: FunctionComponent<ReportsPageProps> = (props: ReportsPageProps): ReactElement => {
	// State
	const [activeTab, setActiveTab] = useState<ReportsTabs>(ReportsTabs.Import);
	const [reportType, setReportType] = useState<string>("claims-report");
	const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonthDisplay());
	const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
	const [currentViewYear, setCurrentViewYear] = useState<number>(new Date().getFullYear());
	const [isImportLoading, setIsImportLoading] = useState<boolean>(false);
	const [importSummary, setImportSummary] = useState<ImportSummary | undefined>(undefined);
	const [importErrors, setImportErrors] = useState<Array<ClaimReportImportError>>([]);
	const [importFatalError, setImportFatalError] = useState<string>("");
	const { isOpen: isMonthPickerOpen, onOpen: onMonthPickerOpen, onClose: onMonthPickerClose } = useDisclosure();
	const { isOpen: isImportModalOpen, onOpen: onImportModalOpen, onClose: onImportModalClose } = useDisclosure();
	const toast = useToast();
	const initialFocusRef = useRef(null);

	/**
	 * Helper function to get the current month in the format "MMM YYYY"
	 * @returns string in the format "MMM YYYY"
	 */
	function getCurrentMonthDisplay(): string {
		const date = new Date();
		const month = date.toLocaleString("default", { month: "short" });
		const year = date.getFullYear();
		return `${month}. ${year}`;
	}

	/**
	 * Handles the report type selection change
	 * @param event Change event from the select input
	 */
	const handleReportTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
		setReportType(event.target.value);
	};

	/**
	 * Handles the generate report button click
	 */
	const handleGenerateReport = () => {
		// In a real implementation, this would call an API to generate the report
		toast({
			title: "Success",
			description: "Successfully downloaded report",
			status: "success",
			duration: 5000,
			isClosable: true,
			position: "top"
		});
	};

	const handleImportReports = async (csvFile: File): Promise<void> => {
		setIsImportLoading(true);
		setImportSummary(undefined);
		setImportErrors([]);
		setImportFatalError("");

		try {
			const response = await importClaimReports(csvFile);
			const summary: ImportSummary = {
				totalRows: response.getTotalRows(),
				successfulRows: response.getSuccessfulRows(),
				failedRows: response.getFailedRows()
			};
			const errors: Array<ClaimReportImportError> = response.getErrorsList().map((error) => ({
				rowNumber: error.getRowNumber(),
				claimId: error.getClaimId(),
				reportName: error.getReportName(),
				reportedOn: error.getReportedOn(),
				type: error.getType(),
				errorMessage: error.getErrorMessage()
			}));

			setImportSummary(summary);
			setImportErrors(errors);

			const completionMessage = `Imported ${summary.successfulRows} of ${summary.totalRows} reports.`;
			toast({
				title: summary.failedRows > 0 ? "Import Complete" : "Success",
				description: completionMessage,
				status: summary.failedRows > 0 ? "warning" : "success",
				duration: 5000,
				isClosable: true,
				position: "top"
			});
		} catch (err) {
			const message = getImportErrorMessage(err);
			setImportFatalError(message);
			toast({
				title: "Error",
				description: message,
				status: "error",
				duration: 7000,
				isClosable: true,
				position: "top"
			});
		} finally {
			setIsImportLoading(false);
		}
	};

	/**
	 * Handles the month selection
	 * @param month Month number (0-11)
	 */
	const handleMonthSelect = (month: number) => {
		const date = new Date(currentViewYear, month, 1);
		const monthName = date.toLocaleString("default", { month: "short" });
		setSelectedMonth(`${monthName}. ${currentViewYear}`);
		setSelectedYear(currentViewYear);
		onMonthPickerClose();
	};

	/**
	 * Handles year navigation
	 * @param direction 1 for next year, -1 for previous year
	 */
	const handleYearChange = (direction: number) => {
		setCurrentViewYear(currentViewYear + direction);
	};

	/**
	 * Renders the month grid for selection
	 * @returns Grid of month buttons
	 */
	const renderMonthGrid = (): ReactElement => {
		const months = [
			{ name: "Jan", value: 0 },
			{ name: "Feb", value: 1 },
			{ name: "Mar", value: 2 },
			{ name: "Apr", value: 3 },
			{ name: "May", value: 4 },
			{ name: "Jun", value: 5 },
			{ name: "Jul", value: 6 },
			{ name: "Aug", value: 7 },
			{ name: "Sep", value: 8 },
			{ name: "Oct", value: 9 },
			{ name: "Nov", value: 10 },
			{ name: "Dec", value: 11 }
		];

		// Check if a month is selected in the current view year
		const isMonthSelected = (month: number): boolean => {
			if (currentViewYear !== selectedYear) return false;
			const selectedMonthName = selectedMonth.split(".")[0].trim();
			const monthName = new Date(currentViewYear, month, 1).toLocaleString("default", { month: "short" });
			return selectedMonthName === monthName;
		};

		// Check if a month is active (if it is current or past)
		const isMonthActive = (month: number): boolean => {
			const currentMonth = new Date().getMonth();
			const currentYear = new Date().getFullYear();
			return currentViewYear < currentYear || (currentViewYear === currentYear && month <= currentMonth);
		};

		return (
			<VStack spacing={0} align="stretch">
				<Flex justify="space-between" align="center" p={2}>
					<ChevronLeftIcon cursor="pointer" onClick={() => handleYearChange(-1)} boxSize={5} />
					<Text fontWeight="medium">{currentViewYear}</Text>
					<ChevronRightIcon cursor="pointer" onClick={() => handleYearChange(1)} boxSize={5} />
				</Flex>
				<Grid templateColumns="repeat(3, 1fr)" gap={1}>
					{months.map((month) => (
						<Box
							key={month.value}
							p={2}
							textAlign="center"
							cursor={isMonthActive(month.value) ? "pointer" : "not-allowed"}
							bg={isMonthSelected(month.value) ? "gray.100" : "white"}
							_hover={{ bg: "gray.50" }}
							opacity={isMonthActive(month.value) ? "1" : "0.5"}
							onClick={() => isMonthActive(month.value) && handleMonthSelect(month.value)}>
							{month.name}
						</Box>
					))}
				</Grid>
			</VStack>
		);
	};

	return (
		<>
			<LayoutWrapper>
				<Box>
					<Box mx={8}>
						<Tabs
							colorScheme="orange"
							variant="enclosed-colored"
							margin="20px 10px"
							isLazy={true}
							index={activeTab}
							onChange={(index: number) => setActiveTab(index)}>
							<Box display="flex" borderColor="transparent" marginLeft="1px">
								<TabList marginLeft="15px" position="relative">
									<Tab
										borderTopWidth="0px"
										backgroundColor="gray.200"
										fontWeight="bold"
										fontSize="19px"
										_selected={{ bg: "white" }}>
										Import
									</Tab>
									<Tab
										borderTopWidth="0px"
										backgroundColor="gray.200"
										fontWeight="bold"
										fontSize="19px"
										marginLeft="4px"
										_selected={{ bg: "white" }}>
										Export
									</Tab>
									<Tab
										position="absolute"
										backgroundColor="#e9e9e9"
										zIndex="-1"
										left="0"
										right="0"
										bottom="0"
										top="0"
										boxShadow="1px 2px 9px 1px gainsboro"
									/>
								</TabList>
							</Box>
							<TabPanels>
								<TabPanel pt="0">
									<TabPage>
										<VStack spacing={4} align="stretch">
											<Flex justifyContent="space-between" alignItems="center">
												<Text fontSize="sm" color="gray.600">
													Upload a CSV with row-level claimId values to import claim reports.
												</Text>
												<Button colorScheme="orange" onClick={onImportModalOpen}>
													Import Reports
												</Button>
											</Flex>

											{isImportLoading && (
												<Flex minH="220px" justifyContent="center" alignItems="center">
													<VStack spacing={3}>
														<Spinner size="xl" color="orange.400" thickness="4px" />
														<Text color="gray.600">Importing reports...</Text>
													</VStack>
												</Flex>
											)}

											{!isImportLoading && importFatalError && (
												<Alert status="error" borderRadius="md">
													<AlertIcon />
													<Text>{importFatalError}</Text>
												</Alert>
											)}

											{!isImportLoading && importSummary && (
												<>
													<HStack spacing={6} alignItems="center">
														<Text fontWeight="600">Total: {importSummary.totalRows}</Text>
														<Text color="green.600" fontWeight="600">
															Imported: {importSummary.successfulRows}
														</Text>
														<Text color={importSummary.failedRows > 0 ? "red.600" : "gray.600"} fontWeight="600">
															Failed: {importSummary.failedRows}
														</Text>
													</HStack>

													{importErrors.length > 0 ? (
														<TableContainer
															border="1px solid"
															borderColor="gray.200"
															borderRadius="md"
															maxH="360px"
															overflowY="auto"
															overflowX="auto">
															<Table size="sm">
																<Thead>
																	<Tr>
																		<Th>Row</Th>
																		<Th>Claim ID</Th>
																		<Th>Report Name</Th>
																		<Th>Reported On</Th>
																		<Th>Type</Th>
																		<Th>Error</Th>
																	</Tr>
																</Thead>
																<Tbody>
																	{importErrors.map((error, index) => (
																		<Tr key={`${error.rowNumber}-${error.claimId}-${index}`}>
																			<Td>{error.rowNumber}</Td>
																			<Td>{error.claimId}</Td>
																			<Td>{error.reportName}</Td>
																			<Td>{error.reportedOn}</Td>
																			<Td>{error.type}</Td>
																			<Td>{error.errorMessage}</Td>
																		</Tr>
																	))}
																</Tbody>
															</Table>
														</TableContainer>
													) : (
														<Text color="green.600" fontWeight="500">
															No row-level errors were returned.
														</Text>
													)}
												</>
											)}

											{!isImportLoading && !importSummary && !importFatalError && (
												<Text fontStyle="italic" color="gray.600">
													No import results yet.
												</Text>
											)}
										</VStack>
									</TabPage>
								</TabPanel>
								<TabPanel pt="0">
									<TabPage>
										<Flex width="100%" justifyContent="center" alignItems="center">
											<HStack spacing={6} mb={6} align="flex-end">
												<Box>
													<Text fontSize="0.9rem" color="gray.500" fontWeight={500} mb={1}>
														Report
													</Text>
													<Select
														value={reportType}
														onChange={handleReportTypeChange}
														w="180px"
														icon={<ChevronDownIcon />}>
														<option value="claims-report">Claims Report</option>
														<option value="payments-report">Payments Report</option>
														<option value="providers-report">Providers Report</option>
													</Select>
												</Box>

												<Box>
													<Text fontSize="0.9rem" color="gray.500" fontWeight={500} mb={1}>
														Month
													</Text>
													<Popover
														isOpen={isMonthPickerOpen}
														onClose={onMonthPickerClose}
														onOpen={onMonthPickerOpen}
														initialFocusRef={initialFocusRef}
														placement="bottom-start">
														<PopoverTrigger>
															<Box
																border="1px"
																borderColor="gray.200"
																borderRadius="md"
																p={2}
																w="180px"
																cursor="pointer"
																display="flex"
																justifyContent="space-between"
																alignItems="center">
																<Text>{selectedMonth}</Text>
																<ChevronDownIcon />
															</Box>
														</PopoverTrigger>
														<PopoverContent width="auto">
															<PopoverBody p={2}>{renderMonthGrid()}</PopoverBody>
														</PopoverContent>
													</Popover>
												</Box>

												<Button colorScheme="orange" onClick={handleGenerateReport}>
													Generate Report
												</Button>
											</HStack>
										</Flex>
									</TabPage>
								</TabPanel>
							</TabPanels>
						</Tabs>
						<ImportReportsModal
							isOpen={isImportModalOpen}
							onClose={onImportModalClose}
							onImport={handleImportReports}
						/>
					</Box>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default ReportsPage;
